import React, { useMemo } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import {
  Box,
  Chip,
  Paper,
  Stack,
  Tab,
  Tabs,
  Toolbar,
  Typography,
} from "@mui/material";
import {
  useLocation,
  useNavigate,
} from "react-router-dom";

import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import ViewListRoundedIcon from "@mui/icons-material/ViewListRounded";
import PersonSearchRoundedIcon from "@mui/icons-material/PersonSearchRounded";
import HandshakeRoundedIcon from "@mui/icons-material/HandshakeRounded";
import BlockRoundedIcon from "@mui/icons-material/BlockRounded";

import ProspectiveCustomers from "../component/Prospective_customers";
import ExistingCustomers from "../component/Existing_Customers";
import StoppedCustomers from "../component/Stopped_customers";
import AllCustomers from "../component/AllCustomers";

import { designTokens } from "../theme";

type CustomerTab =
  | "all"
  | "prospective"
  | "existing"
  | "stopped";

const customerTabs = [
  {
    value: "all" as const,
    label: "全体顧客",
    description: "見込み顧客と既存顧客をまとめて確認します。",
    icon: <ViewListRoundedIcon fontSize="small" />,
  },
  {
    value: "prospective" as const,
    label: "見込み顧客",
    description: "営業活動中・商談前の顧客を管理します。",
    icon: <PersonSearchRoundedIcon fontSize="small" />,
  },
  {
    value: "existing" as const,
    label: "既存顧客",
    description: "取引中の顧客情報と対応状況を確認します。",
    icon: <HandshakeRoundedIcon fontSize="small" />,
  },
  {
    value: "stopped" as const,
    label: "取引停止",
    description: "取引停止中の顧客と停止理由を確認します。",
    icon: <BlockRoundedIcon fontSize="small" />,
  },
];

const isCustomerTab = (
  value: string | null,
): value is CustomerTab =>
  customerTabs.some((tab) => tab.value === value);

const AllCustomersPlaceholder = () => (
  <Paper
    elevation={0}
    sx={{
      minHeight: 280,
      p: { xs: 2.5, md: 4 },
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      textAlign: "center",
      borderRadius: 3,
      bgcolor: designTokens.surface.paper,
      border: `1px dashed ${designTokens.border.strong}`,
    }}
  >
    <Stack spacing={1} alignItems="center">
      <GroupsRoundedIcon
        sx={{ fontSize: 42, color: designTokens.text.muted }}
      />
      <Typography
        sx={{ color: designTokens.text.primary, fontWeight: 900 }}
      >
        全体顧客一覧
      </Typography>
      <Typography
        sx={{ color: designTokens.text.secondary, fontSize: 13 }}
      >
        見込み顧客と既存顧客を統合する一覧は、次の工程で実装します。
      </Typography>
    </Stack>
  </Paper>
);

export default function Customers() {
  const { isAuthenticated } = useAuth0();
  const location = useLocation();
  const navigate = useNavigate();

  const selectedTab = useMemo<CustomerTab>(() => {
    const tab = new URLSearchParams(location.search).get("tab");
    return isCustomerTab(tab) ? tab : "all";
  }, [location.search]);

  if (!isAuthenticated) {
    return null;
  }

  const selectedDefinition =
    customerTabs.find((tab) => tab.value === selectedTab) ??
    customerTabs[0];

  const handleChange = (
    _: React.SyntheticEvent,
    value: CustomerTab,
  ) => {
    navigate(`/customers?tab=${value}`, { replace: true });
  };

  const content = (() => {
    switch (selectedTab) {
      case "prospective":
        return <ProspectiveCustomers />;
      case "existing":
        return <ExistingCustomers />;
      case "stopped":
        return <StoppedCustomers />;
      case "all":
      default:
        return <AllCustomers />;
    }
  })();

  return (
    <Box
      sx={{
        minHeight: "100dvh",
        background:
          "radial-gradient(circle at top right, rgba(152,188,46,0.07), transparent 28%), #F4F6F8",
      }}
    >
      <Toolbar />

      <Box
        sx={{
          px: { xs: 1.5, md: 2.5 },
          py: { xs: 1.5, md: 2 },
        }}
      >
        <Box sx={{ width: "100%", maxWidth: 1480, mx: "auto" }}>
          <Paper
            elevation={0}
            sx={{
              mb: 1.5,
              borderRadius: 3,
              position: "relative",
              overflow: "hidden",
              background:
                "linear-gradient(115deg, rgba(255,255,255,0.98), rgba(247,250,242,0.98) 62%, rgba(235,243,225,0.98))",
              border: `1px solid ${designTokens.border.default}`,
              boxShadow: "0 8px 24px rgba(15,23,42,0.06)",
              "&::before": {
                content: '""',
                position: "absolute",
                inset: 0,
                opacity: 0.35,
                pointerEvents: "none",
                backgroundImage:
                  "radial-gradient(rgba(93,116,62,0.14) 0.7px, transparent 0.7px)",
                backgroundSize: "15px 15px",
                maskImage:
                  "linear-gradient(90deg, rgba(0,0,0,0.65), transparent 60%)",
                WebkitMaskImage:
                  "linear-gradient(90deg, rgba(0,0,0,0.65), transparent 60%)",
              },
            }}
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              alignItems={{ xs: "flex-start", sm: "center" }}
              justifyContent="space-between"
              spacing={1.5}
              sx={{
                position: "relative",
                zIndex: 1,
                p: { xs: 1.6, md: 2 },
              }}
            >
              <Stack direction="row" spacing={1.25} alignItems="center">
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    display: "grid",
                    placeItems: "center",
                    flexShrink: 0,
                    borderRadius: 2,
                    color: designTokens.brand.dark,
                    backgroundColor: designTokens.overlay.brandSoft,
                    border: `1px solid ${designTokens.overlay.brandMedium}`,
                  }}
                >
                  <GroupsRoundedIcon />
                </Box>

                <Box>
                  <Typography
                    component="h1"
                    sx={{
                      color: designTokens.text.primary,
                      fontSize: { xs: 20, md: 24 },
                      fontWeight: 900,
                    }}
                  >
                    顧客一覧
                  </Typography>

                  <Typography
                    sx={{
                      mt: 0.25,
                      color: designTokens.text.secondary,
                      fontSize: 13,
                    }}
                  >
                    {selectedDefinition.description}
                  </Typography>
                </Box>
              </Stack>

              <Chip
                size="small"
                label={selectedDefinition.label}
                sx={{
                  color: designTokens.brand.dark,
                  backgroundColor: designTokens.overlay.brandSoft,
                  border: `1px solid ${designTokens.overlay.brandMedium}`,
                  fontWeight: 800,
                }}
              />
            </Stack>

            <Box
              sx={{
                position: "relative",
                zIndex: 1,
                borderTop: `1px solid ${designTokens.border.default}`,
                backgroundColor: "rgba(255,255,255,0.72)",
                overflowX: "auto",
              }}
            >
              <Tabs
                value={selectedTab}
                onChange={handleChange}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                aria-label="顧客区分"
                sx={{
                  minHeight: 56,
                  px: { xs: 0.5, md: 1 },
                  "& .MuiTabs-indicator": {
                    height: 3,
                    borderRadius: "3px 3px 0 0",
                    backgroundColor: designTokens.brand.dark,
                  },
                  "& .MuiTab-root": {
                    minHeight: 56,
                    minWidth: { xs: 140, sm: 165 },
                    color: designTokens.text.secondary,
                    fontWeight: 800,
                    textTransform: "none",
                    borderRadius: 2,
                    mx: 0.25,
                    "&:hover": {
                      color: designTokens.brand.dark,
                      backgroundColor: designTokens.overlay.brandSoft,
                    },
                    "&.Mui-selected": {
                      color: designTokens.brand.dark,
                    },
                  },
                }}
              >
                {customerTabs.map((tab) => (
                  <Tab
                    key={tab.value}
                    value={tab.value}
                    icon={tab.icon}
                    iconPosition="start"
                    label={tab.label}
                  />
                ))}
              </Tabs>
            </Box>
          </Paper>

          {content}
        </Box>
      </Box>
    </Box>
  );
}
